import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";

import "@material/web/progress/circular-progress.js";
import "@material/web/button/filled-button.js";
import "@material/web/button/outlined-button.js";
import "@material/web/textfield/outlined-text-field.js";
import "@material/web/icon/icon.js";

import "../components/app-shell";
import { api } from "../lib/api";
import { pb } from "../lib/pb";
import { navigate } from "../lib/navigation";
import { sharedStyles } from "../styles/shared";
import { inputValue, errorMessage } from "../lib/dom";
import { icon } from "../lib/icons";
import { toast } from "../lib/toast";
import { AuthController } from "../lib/auth-controller";
import { getNavCounts, type NavCounts } from "../lib/nav-counts";
import type {
  Product,
  DppStatus,
  NfcTag,
  OrgEbsiStatus,
  ScanSummary,
} from "../lib/types";

type PassportState = DppStatus | "none";

// Tag reconciliation buckets (admin-console design): a written tag that has
// been read at least once is reconciled; written but never read is awaiting;
// anything that failed or was destroyed at provisioning is anomalous.
interface Recon {
  reconciled: number;
  awaiting: number;
  anomalous: number;
  total: number;
}

@customElement("dashboard-page")
export class DashboardPage extends LitElement {
  static styles = [
    sharedStyles,
    css`
      /* Protection-layers strip — the anti-counterfeit → GI → DPP hierarchy,
         made concrete with the org's current state per layer. */
      .layers {
        margin-bottom: 22px;
      }
      .layers-lede {
        margin: 0 0 14px;
        max-width: 74ch;
        font-size: 0.875rem;
        color: var(--md-sys-color-on-surface-variant);
      }
      .layer-grid {
        display: grid;
        gap: 12px;
        grid-template-columns: 1fr;
      }
      @media (min-width: 760px) {
        .layer-grid {
          grid-template-columns: repeat(3, 1fr);
        }
      }
      .layer {
        position: relative;
        display: block;
        text-decoration: none;
        color: inherit;
        padding: 16px 18px;
        border-radius: 16px;
        background: var(--md-sys-color-surface-container);
        border: 1px solid var(--md-sys-color-outline-variant);
        transition:
          border-color 0.15s ease,
          transform 0.15s ease;
      }
      .layer:hover,
      .layer:focus-visible {
        border-color: color-mix(
          in srgb,
          var(--md-sys-color-primary) 45%,
          var(--md-sys-color-outline-variant)
        );
        transform: translateY(-2px);
        outline: none;
      }
      .layer__top {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 8px;
      }
      .layer__icon {
        display: inline-flex;
        --md-icon-size: 22px;
        color: var(--md-sys-color-primary);
      }
      .layer__title {
        margin: 0;
        flex: 1;
        font: 600 15px/1.2 var(--font-plain, sans-serif);
      }
      .layer__desc {
        margin: 0;
        font-size: 0.82rem;
        line-height: 1.45;
        color: var(--md-sys-color-on-surface-variant);
      }
      .layer__state {
        margin-top: 12px;
      }
      .layer__arrow {
        position: absolute;
        top: 16px;
        inset-inline-end: 14px;
        opacity: 0.45;
        --md-icon-size: 18px;
      }
      :host([dir="rtl"]) .layer__arrow {
        transform: scaleX(-1);
      }
      .recon-pad {
        padding: 4px 20px 20px;
      }
      .ebsi-pad {
        padding: 4px 20px 20px;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
      }
      .ebsi-pad p {
        margin: 0;
        color: var(--md-sys-color-on-surface-variant);
        font-size: 0.875rem;
        max-width: 60ch;
      }
      .ebsi-did {
        font-family: ui-monospace, monospace;
        font-size: 0.8125rem;
        word-break: break-all;
      }

      .scan-pad {
        padding: 4px 20px 20px;
      }
      .scan-figures {
        display: flex;
        gap: 28px;
        flex-wrap: wrap;
        margin-bottom: 16px;
      }
      .scan-figures div b {
        display: block;
        font-size: 1.5rem;
        font-weight: 600;
        line-height: 1.2;
      }
      .scan-figures div span {
        font-size: 0.75rem;
        color: var(--md-sys-color-on-surface-variant);
      }
      /* Daily activity: one column per day, stacked genuine + suspicious.
         2px gaps between columns and between the two segments (mark spec). */
      .chart {
        position: relative;
        display: flex;
        align-items: flex-end;
        gap: 2px;
        height: 96px;
      }
      .chart .col {
        flex: 1;
        min-width: 0;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        gap: 2px;
        cursor: default;
      }
      .chart .col:hover {
        background: color-mix(
          in srgb,
          var(--md-sys-color-on-surface) 6%,
          transparent
        );
      }
      .chart .seg {
        border-radius: 4px 4px 0 0;
        min-height: 2px;
      }
      .chart .seg--genuine {
        background: var(--md-sys-color-primary);
      }
      /* Suspicious taps carry a 45° hatch as well as the error hue: in dark
         mode the M3 primary/error pair sits at ΔE 5.2 under protanopia, so
         color alone would not separate the two segments. */
      .chart .seg--suspicious {
        background: var(--md-sys-color-error);
        background-image: repeating-linear-gradient(
          45deg,
          transparent 0 3px,
          color-mix(in srgb, var(--md-sys-color-on-error) 55%, transparent) 3px
            5px
        );
      }
      .chart-tip {
        position: absolute;
        bottom: calc(100% + 6px);
        transform: translateX(-50%);
        background: var(--md-sys-color-inverse-surface);
        color: var(--md-sys-color-inverse-on-surface);
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 0.75rem;
        line-height: 1.35;
        white-space: nowrap;
        pointer-events: none;
        z-index: 2;
      }
      .legend i.hatch {
        background-image: repeating-linear-gradient(
          45deg,
          transparent 0 2px,
          color-mix(in srgb, var(--md-sys-color-on-error) 55%, transparent) 2px
            4px
        );
      }
      .alerts {
        margin: 16px 0 0;
        padding: 0;
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .alerts li {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 0.8125rem;
        flex-wrap: wrap;
      }
      .alerts time {
        color: var(--md-sys-color-on-surface-variant);
      }
      /* Counterfeit findings: stated conclusions, not chart furniture, so they
         sit above the activity plot in an error-toned container. */
      .findings {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-bottom: 16px;
      }
      .finding {
        border-radius: 10px;
        padding: 12px 14px;
        background: var(--md-sys-color-error-container);
        color: var(--md-sys-color-on-error-container);
      }
      .finding b {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 0.875rem;
      }
      .finding p {
        margin: 4px 0 0;
        font-size: 0.8125rem;
        line-height: 1.45;
        max-width: 72ch;
      }
      .finding small {
        display: block;
        margin-top: 6px;
        opacity: 0.8;
        font-size: 0.75rem;
      }
      .fakes {
        margin: 8px 0 0;
        padding: 0;
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 4px;
        font-size: 0.75rem;
      }
      .fakes li {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
        align-items: baseline;
        opacity: 0.9;
      }
      .fake-uid {
        font-family: ui-monospace, monospace;
      }
      .fake-note {
        font-style: italic;
      }
    `,
  ];

  private auth = new AuthController(this);

  @state() private products: Product[] = [];
  @state() private statusByProduct: Record<string, PassportState> = {};
  @state() private tags: NfcTag[] = [];
  @state() private scans: ScanSummary | null = null;
  @state() private hoverDay = -1;
  @state() private ebsi: OrgEbsiStatus | null = null;
  @state() private requestingEbsi = false;
  @state() private filter = "";
  @state() private loading = true;
  @state() private error = "";
  @state() private caps: NavCounts | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    void this.load();
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.error = "";
    try {
      // One round trip, not two. These were two Promise.all groups in SERIES,
      // so the second waited on the first for no reason — none of them depend
      // on each other, and the wait was a whole network round trip the user
      // spent looking at a spinner. The last two are best-effort: their cards
      // stay hidden if the call fails.
      const [products, versions, tagResp, ebsi, scans] = await Promise.all([
        api.get<Product[]>("/catalog/products"),
        pb.collection("dpp_versions").getFullList<{
          product: string;
          status: DppStatus;
        }>({ requestKey: "dashboard-passport-status" }),
        api.get<{ tags: NfcTag[] | null }>("/nfc/tags"),
        api.get<OrgEbsiStatus>("/provisioning/my/ebsi").catch(() => null),
        api.get<ScanSummary>("/nfc/scans/summary?days=30").catch(() => null),
      ]);
      this.ebsi = ebsi;
      this.scans = scans;
      const rank: Record<DppStatus, number> = {
        published: 3,
        draft: 2,
        deactivated: 1,
      };
      const statusByProduct: Record<string, PassportState> = {};
      for (const v of versions) {
        const current = statusByProduct[v.product];
        if (
          current === undefined ||
          rank[v.status] > rank[current as DppStatus]
        ) {
          statusByProduct[v.product] = v.status;
        }
      }
      this.products = products;
      this.statusByProduct = statusByProduct;
      this.tags = tagResp.tags ?? [];
      // Capability signals for the protection-layers strip. Session-cached
      // (the shell already fetched it), best-effort — the strip degrades to
      // "available" states if it fails.
      void getNavCounts().then((c) => {
        this.caps = c;
      });
    } catch (err) {
      this.error = errorMessage(err, "Failed to load dashboard.");
    } finally {
      this.loading = false;
    }
  }

  private get recon(): Recon {
    let reconciled = 0;
    let awaiting = 0;
    let anomalous = 0;
    for (const t of this.tags) {
      if (t.TerminalState !== "written_ok") anomalous += 1;
      else if (t.CounterLastSeen > 0) reconciled += 1;
      else awaiting += 1;
    }
    return { reconciled, awaiting, anomalous, total: this.tags.length };
  }

  private tagCount(productID: string): number {
    return this.tags.filter((t) => t.ProductID === productID).length;
  }

  private passportState(productID: string): PassportState {
    return this.statusByProduct[productID] ?? "none";
  }

  private passportBadge(state: PassportState) {
    switch (state) {
      case "published":
        return html`<span class="badge badge--ok"
          >${icon("verified")} Published</span
        >`;
      case "draft":
        return html`<span class="badge badge--neutral"
          >${icon("edit")} Draft</span
        >`;
      case "deactivated":
        return html`<span class="badge badge--bad"
          >${icon("flag")} Deactivated</span
        >`;
      default:
        return html`<span class="badge badge--neutral"
          >${icon("hourglass_empty")} No passport</span
        >`;
    }
  }

  private get filteredProducts(): Product[] {
    const q = this.filter.trim().toLowerCase();
    if (!q) return this.products;
    return this.products.filter((p) =>
      `${p.Name} ${p.SKU} ${this.passportState(p.ID)}`
        .toLowerCase()
        .includes(q),
    );
  }

  // Client-side CSV export of the product list with passport state + tag count.
  private exportCSV(): void {
    const esc = (v: unknown) => `"${String(v ?? "").replaceAll('"', '""')}"`;
    const rows = [
      ["Name", "SKU", "Passport", "NFC tags", "Created"],
      ...this.products.map((p) => [
        p.Name,
        p.SKU,
        this.passportState(p.ID),
        this.tagCount(p.ID),
        p.CreatedAt,
      ]),
    ];
    const csv = rows.map((r) => r.map(esc).join(",")).join("\r\n");
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = "products.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  // Hands off to the products page with a one-shot "open the create dialog"
  // intent (the router matches pathnames only, so no query string).
  private newProduct(): void {
    sessionStorage.setItem("dpp:create-product", "1");
    void navigate("/products");
  }

  private formatDate(value: string): string {
    const d = new Date(value);
    if (isNaN(d.getTime()) || d.getFullYear() < 1970) return "—";
    return d.toLocaleDateString();
  }

  private async requestEbsi(): Promise<void> {
    this.requestingEbsi = true;
    try {
      this.ebsi = await api.post<OrgEbsiStatus>(
        "/provisioning/my/ebsi/request",
        {},
      );
      toast("EBSI onboarding requested — our team will take it from here.");
    } catch (err) {
      toast(errorMessage(err, "Failed to request EBSI onboarding."));
    } finally {
      this.requestingEbsi = false;
    }
  }

  // Announced, not offered: no request button, because there is nothing behind
  // it yet. Promising a click that quietly does nothing is how a trust feature
  // loses trust.
  private renderEbsiComingSoon() {
    return html`
      <div class="card card--flush">
        <div class="cardhead">
          <h2>EBSI trust status</h2>
          <span class="badge badge--neutral">Coming soon</span>
        </div>
        <div class="ebsi-pad">
          <p>
            Anchoring on the EU Blockchain (EBSI) is being prepared. Once it is
            live, your organization can be onboarded as a trusted issuer and
            every published passport will carry a verifiable credential signed
            under your own decentralised identifier.
          </p>
          <p class="muted">
            Nothing is required from you now — your products, tags and passports
            are unaffected, and we will invite you when onboarding opens.
          </p>
        </div>
      </div>
    `;
  }

  // The org's EBSI trust card.
  //
  // When EBSI is not wired and the org has never onboarded, the card announces
  // the capability as COMING SOON rather than hiding or offering a button that
  // cannot work. Hiding it entirely was the old behaviour and it was honest but
  // silent: a producer evaluating us could not tell whether EU-blockchain
  // anchoring was missing, broken, or simply not built yet. An org that DOES
  // have onboarding history keeps its real status, because that is a fact about
  // them rather than a roadmap.
  private renderEbsi() {
    const e = this.ebsi;
    if (!e) return null;
    if (!e.ebsiConfigured && e.status === "none") return this.renderEbsiComingSoon();
    const badge = {
      active: html`<span class="badge badge--ok"
        >${icon("verified")} Trusted issuer</span
      >`,
      onboarding: html`<span class="badge badge--neutral"
        >${icon("hourglass_empty")} Onboarding</span
      >`,
      requested: html`<span class="badge badge--neutral"
        >${icon("hourglass_empty")} Requested</span
      >`,
      revoked: html`<span class="badge badge--bad"
        >${icon("flag")} Revoked</span
      >`,
      none: html`<span class="badge badge--neutral">Not onboarded</span>`,
    }[e.status];
    return html`
      <div class="card card--flush">
        <div class="cardhead">
          <h2>EBSI trust status</h2>
          ${badge}
        </div>
        <div class="ebsi-pad">
          ${e.status === "active"
            ? html`<p>
                Your organization is anchored on EBSI as a trusted issuer.
                Published passports carry verifiable credentials signed by
                <span class="ebsi-did">${e.did}</span>.
              </p>`
            : e.status === "onboarding"
              ? html`<p>
                  EBSI onboarding is in progress. Verification usually takes a
                  few business days.
                </p>`
              : e.status === "requested"
                ? html`<p>
                    Your onboarding request from
                    ${this.formatDate(e.requestedAt ?? "")} is with our
                    operations team.
                  </p>`
                : e.status === "revoked"
                  ? html`<p>
                      Your EBSI accreditation was revoked${e.statusReason
                        ? html` — ${e.statusReason}`
                        : null}.
                      Contact support to restore it.
                    </p>`
                  : html`<p>
                      Anchor your organization on the EU Blockchain (EBSI) so
                      every published passport carries a verifiable
                      credential.
                    </p>`}
          ${e.status === "none" && this.auth.hasRole("org_admin")
            ? html`<md-filled-button
                @click=${() => this.requestEbsi()}
                ?disabled=${this.requestingEbsi}
              >
                <span slot="icon">${icon("verified")}</span>
                ${this.requestingEbsi
                  ? "Requesting…"
                  : "Request EBSI onboarding"}
              </md-filled-button>`
            : null}
        </div>
      </div>
    `;
  }

  // Producer-facing wording for a verdict — "replay" is jargon; what the
  // producer needs to know is that a tag looked cloned.
  private static readonly SCAN_VERDICTS: Record<string, string> = {
    forged: "Forgery attempt",
    revoked: "Retired tag used",
    replay: "Link re-used",
    tampered: "Tamper detected",
    invalid: "Failed verification",
  };

  private productName(id: string): string {
    return this.products.find((p) => p.ID === id)?.Name ?? "Unknown product";
  }

  // Keep the tooltip inside the card: centred over its column in the middle of
  // the chart, but edge-anchored near either end so it cannot overflow.
  private tipPosition(index: number, count: number): string {
    const pct = ((index + 0.5) / count) * 100;
    if (pct > 85) return "right:0;transform:none";
    if (pct < 15) return "left:0;transform:none";
    return `left:${pct}%`;
  }

  private formatDay(iso: string): string {
    const d = new Date(iso + "T00:00:00Z");
    return isNaN(d.getTime())
      ? iso
      : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }

  // The two findings a producer can act on, stated plainly rather than buried
  // in the verdict counts. Both are absent in the normal case.
  private renderCounterfeitFindings(s: ScanSummary) {
    if (
      s.clonedTags.length === 0 &&
      s.forgeryAttempts === 0 &&
      s.impersonatingTags.length === 0
    ) {
      return null;
    }
    const totalFakeTaps = s.impersonatingTags.reduce((n, t) => n + t.taps, 0);
    return html`
      <div class="findings">
        ${s.impersonatingTags.length > 0
          ? html`<div class="finding">
              <b>${icon("flag")} ${s.impersonatingTags.length}
                ${s.impersonatingTags.length === 1
                  ? "counterfeit tag"
                  : "counterfeit tags"}
                impersonating your products</b>
              <p>
                ${s.impersonatingTags.length === 1 ? "A tag" : "Tags"} carrying
                ${s.impersonatingTags.length === 1 ? "an ID" : "IDs"} we never
                issued sent
                ${totalFakeTaps === 1
                  ? "a buyer"
                  : `${totalFakeTaps.toLocaleString("en")} buyers`}
                to our verification page. Every genuine tag is registered here,
                so ${s.impersonatingTags.length === 1 ? "this was" : "these were"}
                manufactured to pass as yours. Each buyer was told the item
                could not be verified.
              </p>
              <ul class="fakes">
                ${s.impersonatingTags.map(
                  (t) => html`<li>
                    <span class="fake-uid">${t.uid}</span>
                    <span
                      >${t.taps}
                      ${t.taps === 1 ? "scan" : "scans"}</span
                    >
                    <span>last ${new Date(t.lastSeen).toLocaleDateString()}</span>
                    ${t.attributionNote
                      ? html`<span class="fake-note">${t.attributionNote}</span>`
                      : null}
                  </li>`,
                )}
              </ul>
            </div>`
          : null}
        ${s.clonedTags.map(
          (c) => html`
            <div class="finding finding--clone">
              <b>${icon("flag")} Cloned tag in circulation</b>
              <p>
                A tag on ${this.productName(c.productId)} was seen carrying two
                independently advancing counters — one sequence had reached
                ${c.leadingCounter.toLocaleString("en")} while a second ran
                ${c.counters.join(", ")}. A single chip's counter cannot go
                backwards and climb again, so two physical tags carry this
                identity. Which one is yours is not visible from here: a copy
                that is being sold hard will out-scan the real item and hold the
                higher count.
              </p>
              <small
                >Tag ${c.tagId} · ${new Date(c.firstAt).toLocaleDateString()} –
                ${new Date(c.lastAt).toLocaleDateString()}</small
              >
            </div>
          `,
        )}
        ${s.forgeryAttempts > 0
          ? html`<div class="finding">
              <b>${icon("flag")} ${s.forgeryAttempts}
                ${s.forgeryAttempts === 1 ? "forgery attempt" : "forgery attempts"}</b>
              <p>
                Taps presented one of your tag identities with invalid
                cryptography — whoever tapped had the tag's ID but not its keys.
              </p>
            </div>`
          : null}
      </div>
    `;
  }

  // Consumer scan activity: engagement plus counterfeit signals, over the tap
  // log the verify endpoint already writes.
  private renderScans() {
    const s = this.scans;
    if (!s) return null;
    // Nothing to say to an org with no tags in the field yet.
    if (s.total === 0 && this.tags.length === 0) return null;

    const peak = Math.max(1, ...s.daily.map((d) => d.genuine + d.suspicious));
    const hovered = this.hoverDay >= 0 ? s.daily[this.hoverDay] : null;

    return html`
      <div class="card card--flush">
        <div class="cardhead">
          <h2>Consumer scans <small>· last ${s.windowDays} days</small></h2>
          ${s.suspicious > 0
            ? html`<span class="badge badge--bad"
                >${icon("flag")} ${s.suspicious} suspicious</span
              >`
            : html`<span class="badge badge--ok"
                >${icon("verified")} All genuine</span
              >`}
        </div>
        <div class="scan-pad">
          ${this.renderCounterfeitFindings(s)}
          ${s.total === 0
            ? html`<p class="empty">
                No scans yet. Activity appears here the first time someone taps
                one of your tags.
              </p>`
            : html`
                <div class="scan-figures">
                  <div>
                    <b>${s.total.toLocaleString("en")}</b>
                    <span>Scans</span>
                  </div>
                  <div>
                    <b>${s.uniqueTags.toLocaleString("en")}</b>
                    <span>Tags scanned</span>
                  </div>
                  <div>
                    <b
                      style=${s.suspicious > 0
                        ? "color:var(--md-sys-color-error)"
                        : ""}
                      >${s.suspicious.toLocaleString("en")}</b
                    >
                    <span>Suspicious</span>
                  </div>
                </div>

                <div class="chart" @mouseleave=${() => (this.hoverDay = -1)}>
                  ${s.daily.map((d, i) => {
                    const total = d.genuine + d.suspicious;
                    // A lone suspicious tap on a busy day must stay visible,
                    // so its segment gets a floor rather than a true ratio.
                    const susH = d.suspicious
                      ? Math.max(6, (100 * d.suspicious) / peak)
                      : 0;
                    const genH = d.genuine ? (100 * d.genuine) / peak : 0;
                    return html`<div
                      class="col"
                      @mouseenter=${() => (this.hoverDay = i)}
                      aria-label=${`${d.date}: ${d.genuine} genuine, ${d.suspicious} suspicious`}
                    >
                      ${d.suspicious
                        ? html`<div
                            class="seg seg--suspicious"
                            style="height:${susH}%"
                          ></div>`
                        : null}
                      ${d.genuine
                        ? html`<div
                            class="seg seg--genuine"
                            style="height:${genH}%"
                          ></div>`
                        : null}
                      ${total === 0
                        ? html`<div
                            class="seg"
                            style="height:2px;background:var(--md-sys-color-outline-variant)"
                          ></div>`
                        : null}
                    </div>`;
                  })}
                  ${hovered
                    ? html`<div
                        class="chart-tip"
                        style=${this.tipPosition(
                          this.hoverDay,
                          s.daily.length,
                        )}
                      >
                        <b>${this.formatDay(hovered.date)}</b><br />
                        ${hovered.genuine} genuine${hovered.suspicious
                          ? html` · ${hovered.suspicious} suspicious`
                          : null}
                      </div>`
                    : null}
                </div>
                <div class="legend">
                  <span
                    ><i style="background:var(--md-sys-color-primary)"></i
                    >Genuine</span
                  >
                  <span
                    ><i
                      class="hatch"
                      style="background-color:var(--md-sys-color-error)"
                    ></i
                    >Suspicious</span
                  >
                  <span
                    >${this.formatDay(s.daily[0]?.date ?? "")} –
                    ${this.formatDay(s.daily[s.daily.length - 1]?.date ?? "")}</span
                  >
                </div>

                ${s.alerts.length > 0
                  ? html`<ul class="alerts">
                      ${s.alerts.map(
                        (a) => html`<li>
                          <span class="badge badge--bad"
                            >${icon("flag")}
                            ${DashboardPage.SCAN_VERDICTS[a.result] ??
                            a.result}</span
                          >
                          <span>${this.productName(a.productId)}</span>
                          <time datetime=${a.occurredAt}
                            >${new Date(a.occurredAt).toLocaleString()}</time
                          >
                        </li>`,
                      )}
                    </ul>`
                  : null}
              `}
        </div>
      </div>
    `;
  }

  private renderRecon() {
    const r = this.recon;
    if (r.total === 0) return null;
    const pct = (n: number) => (100 * n) / r.total;
    return html`
      <div class="card card--flush">
        <div class="cardhead">
          <h2>NFC reconciliation</h2>
          ${r.anomalous > 0
            ? html`<span class="badge badge--bad"
                >${icon("flag")} ${r.anomalous} flagged</span
              >`
            : html`<span class="badge badge--ok"
                >${icon("verified")} All clear</span
              >`}
        </div>
        <div class="recon-pad">
          <div class="recon-bar">
            <span
              style="width:${pct(r.reconciled)}%;background:var(--md-sys-color-primary)"
            ></span>
            <span
              style="width:${pct(r.awaiting)}%;background:var(--md-sys-color-outline-variant)"
            ></span>
            <span
              style="width:${pct(r.anomalous)}%;background:var(--md-sys-color-error)"
            ></span>
          </div>
          <div class="legend">
            <span
              ><i style="background:var(--md-sys-color-primary)"></i>Reconciled
              (${r.reconciled})</span
            >
            <span
              ><i style="background:var(--md-sys-color-outline-variant)"></i
              >Awaiting first read (${r.awaiting})</span
            >
            <span
              ><i style="background:var(--md-sys-color-error)"></i>Anomalous
              (${r.anomalous})</span
            >
          </div>
        </div>
      </div>
    `;
  }

  private renderProducts() {
    const list = this.filteredProducts;
    return html`
      <div class="card card--flush">
        <div class="cardhead">
          <h2>Products</h2>
          <md-outlined-text-field
            label="Filter"
            .value=${this.filter}
            @input=${(e: Event) => (this.filter = inputValue(e))}
          >
            <span slot="leading-icon">${icon("search")}</span>
          </md-outlined-text-field>
        </div>
        <div class="table-scroll"><table>
          <thead>
            <tr>
              <th>Product</th>
              <th>Passport</th>
              <th>Tags</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            ${list.length === 0
              ? html`<tr>
                  <td colspan="5" class="empty">
                    ${this.products.length === 0
                      ? "No products yet — create your first product."
                      : `No products match “${this.filter}”`}
                  </td>
                </tr>`
              : list.map(
                  (p) => html`
                    <tr>
                      <td>
                        <b>${p.Name}</b>
                        ${p.SKU ? html`<small>${p.SKU}</small>` : null}
                      </td>
                      <td>${this.passportBadge(this.passportState(p.ID))}</td>
                      <td>${this.tagCount(p.ID).toLocaleString("en")}</td>
                      <td>${this.formatDate(p.CreatedAt)}</td>
                      <td><a href="/products/${p.ID}">View →</a></td>
                    </tr>
                  `,
                )}
          </tbody>
        </table></div>
      </div>
    `;
  }

  // The three capability layers, in priority order. Each is independent and
  // composable — the copy makes the layering explicit: a passport is added on
  // top of a protected/authenticated product, never required by it.
  private renderLayers() {
    const activeTags = this.tags.filter((t) => t.Status === "active").length;
    const passportsLive = Object.values(this.statusByProduct).filter(
      (s) => s === "published",
    ).length;
    const giAuthorized = this.caps?.giAuthorized === true;

    const ok = (text: string) =>
      html`<span class="badge badge--ok">${text}</span>`;
    const idle = (text: string) =>
      html`<span class="badge badge--neutral">${text}</span>`;

    const layer = (
      href: string,
      icon_: string,
      title: string,
      desc: string,
      state: unknown,
    ) => html`
      <a class="layer" href=${href}>
        <span class="layer__arrow">${icon("arrow_forward")}</span>
        <div class="layer__top">
          <span class="layer__icon">${icon(icon_)}</span>
          <h3 class="layer__title">${title}</h3>
        </div>
        <p class="layer__desc">${desc}</p>
        <div class="layer__state">${state}</div>
      </a>
    `;

    return html`
      <section class="layers" aria-label="Protection layers">
        <p class="layers-lede">
          Three independent layers, most-standalone first. Each is optional and
          composable — a Digital Product Passport is added <em>on top of</em> an
          authenticated or GI-protected product, never required by it.
        </p>
        <div class="layer-grid">
          ${layer(
            "/nfc",
            "nfc",
            "Anti-counterfeit",
            "Cryptographic NFC tags prove an item is genuine on every tap. No regulation, no compliance — the flagship layer.",
            activeTags > 0
              ? ok(`${activeTags.toLocaleString("en")} active tags`)
              : idle("Available"),
          )}
          ${layer(
            "/gi",
            "workspace_premium",
            "Geographical Indication",
            "Protected origin and authorized-producer standing, with a trust chain from the authority to you.",
            giAuthorized ? ok("Authorized") : idle("Not authorized"),
          )}
          ${layer(
            "/products",
            "inventory_2",
            "Digital Product Passport",
            "The full ESPR-ready passport a product carries — the regulatory layer, added to any product.",
            passportsLive > 0
              ? ok(`${passportsLive.toLocaleString("en")} published`)
              : idle("Available"),
          )}
        </div>
      </section>
    `;
  }

  render() {
    const r = this.recon;
    const reconciledPct =
      r.total > 0 ? `${((100 * r.reconciled) / r.total).toFixed(1)}%` : "—";
    return html`
      <app-shell active="/">
        <div class="page">
          <div class="page-header">
            <h1>Dashboard</h1>
            <md-outlined-button @click=${() => this.exportCSV()}>
              Export
            </md-outlined-button>
            <md-filled-button @click=${() => this.newProduct()}>
              New product
              <span slot="icon">${icon("add")}</span>
            </md-filled-button>
          </div>

          ${this.loading
            ? html`<div class="center-loading">
                <md-circular-progress indeterminate></md-circular-progress>
              </div>`
            : this.error
              ? html`<p class="error-text">${this.error}</p>`
              : html`
                  ${this.renderLayers()}

                  <div class="stats">
                    <div class="stat">
                      <div class="n">
                        ${this.tags
                          .filter((t) => t.Status === "active")
                          .length.toLocaleString("en")}
                      </div>
                      <div class="l">Active NFC tags</div>
                    </div>
                    <div class="stat">
                      <div class="n">${reconciledPct}</div>
                      <div class="l">Tags reconciled</div>
                    </div>
                    <div class="stat">
                      <div
                        class="n"
                        style=${r.anomalous > 0
                          ? "color:var(--md-sys-color-error)"
                          : ""}
                      >
                        ${r.anomalous}
                      </div>
                      <div class="l">Flagged tags</div>
                    </div>
                    <div class="stat">
                      <div class="n">${this.products.length}</div>
                      <div class="l">Registered products</div>
                    </div>
                  </div>

                  ${this.renderScans()} ${this.renderRecon()}
                  ${this.renderEbsi()} ${this.renderProducts()}
                `}
        </div>
      </app-shell>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "dashboard-page": DashboardPage;
  }
}
